import Button from '@pelckmans/elementis/components/button';
import PlusIcon from '@pelckmans/elementis/icons/plus';
import { useTranslation } from 'react-i18next';
import { useRoutes } from '../../../../hooks/useRoutes';
import EmptyState from '../../../../shared-components/EmptyState';
import { useFilters } from '../context/filter-context/FilterContext';
import ModalContext from '../../../../contexts/ModalContext';
import { useContext } from 'react';
import AddLicenseModal from '../../../../shared-components/AddLicenseModal';
import ModuleContext from '../../_contexts/ModuleContext';

const BASE = 'PAGES.RESULTS.PER_STUDENT.EMPTY_STATE';

const variantKeyMap = {
  'no-groups': 'NO_GROUPS',
  'no-scorable-items': 'NO_SCORABLE_ITEMS',
  'no-scorable-items-filter': 'NO_SCORABLE_ITEMS_FILTER',
  'no-groups-filter': 'NO_GROUPS_FILTER',
  'no-license': 'NO_LICENSE',
} as const;

interface BaseProps {
  variant: Exclude<keyof typeof variantKeyMap, 'no-scorable-items'>;
}

interface NoScorableItemsProps {
  variant: 'no-scorable-items';
  moduleNiceName: string;
}

interface NoResultsProps {
  variant: 'no-scorable-items-filter' | 'no-groups-filter';
  onClear?: () => void;
}

type Props = BaseProps | NoScorableItemsProps | NoResultsProps;

function CallToAction(props: DistributiveOmit<Props, 'moduleNiceName'>) {
  const { t } = useTranslation();
  const { showModal } = useContext(ModalContext);
  const { refetch: refetchModule } = useContext(ModuleContext);

  const { clearFilters } = useFilters();
  const { getRoute } = useRoutes();

  if (props.variant === 'no-license') {
    return (
      <Button
        prominence="secondary"
        startIcon={<PlusIcon />}
        onClick={() => showModal(<AddLicenseModal id="add-license-modal" onRefetch={refetchModule} />)}
      >
        {t(`${BASE}.NO_LICENSE.ACTION`)}
      </Button>
    );
  }

  if (props.variant === 'no-groups') {
    return (
      <Button prominence="secondary" startIcon={<PlusIcon />} as="link" href={`/ianua/${getRoute('MY_GROUPS')}`}>
        {t(`${BASE}.NO_GROUPS.CREATE_GROUP`)}
      </Button>
    );
  }

  function handleClearFilters() {
    clearFilters();
    if ('onClear' in props) {
      props.onClear?.();
    }
  }

  if (props.variant === 'no-scorable-items-filter' || props.variant === 'no-groups-filter') {
    return (
      <Button prominence="neutral" onClick={handleClearFilters}>
        {t(`${BASE}.NO_SCORABLE_ITEMS_FILTER.ACTION`)}
      </Button>
    );
  }

  return null;
}

function ResultsEmptyState(props: Props) {
  const { t } = useTranslation();

  return (
    <EmptyState
      variant="table"
      headerTitle={t(`${BASE}.${variantKeyMap[props.variant]}.TITLE`)}
      bodyText={t(`${BASE}.${variantKeyMap[props.variant]}.BODY`, {
        moduleNiceName: props.variant === 'no-scorable-items' ? props.moduleNiceName : undefined,
      })}
      cta={<CallToAction {...props} />}
    />
  );
}

export default ResultsEmptyState;
